import { useQuery } from "@tanstack/react-query";
import { bridgeApiService } from "@/services/bridge-api.service";
import { ExchangeLimitsRequest } from "@/types/bridge.types";
import { BRIDGE_ASSETS } from "@/config/sharedConfig";

interface BridgeLimitsParams {
  fromNetwork: "solana" | "multiversx";
  toNetwork: "solana" | "multiversx";
  enabled?: boolean;
}

export const useBridgeLimits = ({
  fromNetwork,
  toNetwork,
  enabled = true,
}: BridgeLimitsParams) => {
  return useQuery({
    queryKey: ["bridge-limits", fromNetwork, toNetwork],
    queryFn: async () => {
      const fromNetworkFormatted =
        fromNetwork === "multiversx" ? "egld" : "sol";
      const toNetworkFormatted = toNetwork === "multiversx" ? "egld" : "sol";

      const request: ExchangeLimitsRequest = {
        assetFrom: BRIDGE_ASSETS.A1X,
        networkFrom: fromNetworkFormatted,
        assetTo: BRIDGE_ASSETS.A1X,
        networkTo: toNetworkFormatted,
      };

      try {
        return await bridgeApiService.getExchangeLimits(request);
      } catch (error) {
        // Enhanced error handling for better user experience
        if (error instanceof Error) {
          if (error.message.includes("timeout")) {
            throw new Error(
              "Request timed out. Please check your connection and try again."
            );
          }
          if (error.message.includes("Network Error")) {
            throw new Error(
              "Network connection failed. Please check your internet and try again."
            );
          }
          if (
            error.message.includes("500") ||
            error.message.includes("Internal Server")
          ) {
            throw new Error(
              "Service temporarily unavailable. Please try again in a moment."
            );
          }
          if (
            error.message.includes("400") ||
            error.message.includes("Bad Request")
          ) {
            throw new Error(
              "Invalid request parameters. Please check your input and try again."
            );
          }
          if (error.message.includes("404") || error.message.includes("not_found_pair")) {
            throw new Error(
              "Trading pair not available for the selected networks."
            );
          }
        }
        throw error;
      }
    },
    enabled: enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - limits don't change frequently
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
    retry: (failureCount, error) => {
      // Don't retry for validation errors
      if (error instanceof Error) {
        const message = error.message.toLowerCase();
        if (message.includes("invalid") || 
            message.includes("required") || 
            message.includes("not_found_pair")) {
          return false;
        }
      }
      // Retry network errors up to 2 times
      return failureCount < 2;
    },
    retryDelay: 2000,
  });
};